import DataBindManager, { DataBindTarget, ProxyValueWrapper } from "../index";
import { View } from "../../index";

export abstract class BaseDataBind
{
    /** Name of the attribute to extract the property name from. */
    public abstract attributeName: string;
    /** Selector string used to select all elements the bind listens for changes. */
    public abstract selector: string;
    /** List of DOM event names to listen for changes. */
    public abstract eventNames: string[];

    private _elements: Element[];
    protected get elements(): Element[]
    {
        if (this._elements === undefined)
        {
            this._elements = this.view.querySelector(this.selector);
        }

        return this._elements;
    }

    protected manager: DataBindManager;
    protected view: View;
    protected databinds: DataBindTarget[];

    constructor(manager: DataBindManager, view: View)
    {
        this.manager = manager;
        this.view = view;
        this.databinds = [];
    }

    /** Applies a new value to an element. Abstract method that all inheriting classes must implement.  */
    protected abstract setElementValue(element: Element, value: any);

    /** Extracts a value from an element. Abstract method that all inheriting classes must implement.  */
    protected abstract getElementValue(element: Element): any;

    /**
     * Initiates the process where the value is applied to all the listeners via the view data proxy. 
     * @param propertyName Name of the property that changed.
     * @param value The new value.
     * @param senderElement The element that initiated the value change.
     */
    protected startValueUpdatePropagation(propertyName: string, value: any, senderElement?: Element)
    {
        this.view.data[propertyName] = new ProxyValueWrapper(value, senderElement);
    }
    
    /**
     * Checks whether or not a value should be set to an element.
     * @param element The element the value about to be set to.
     * @param senderElement The element that initiated the value change.
     */
    protected shouldSetElementValue(element: Element, senderElement: Element)
    {
        return element !== senderElement;
    }

    /**
     * Returns the property value of the element.
     * @param element Element to get the property from.
     */
    protected getPropertyValue(element: Element)
    {
        return element.getAttribute(this.attributeName);
    }

    /**
     * Loads the default value. If the value is not set in the view,
     * Last value in the template wins.
     * @param element Element to laod the default value from if view doesn't set it.
     * @param propertyName Name of the property.
     */
    protected loadDefaultValue(bind: DataBindTarget)
    {
        let updateDefaultValue = false;
        let value = this.getElementValue(bind.element);

        this.manager.setDefaultValue(bind, value);
    }

    /**
     * Binds events to the element that report value change in the element.
     * @param element The element to listen.
     * @param propertyName Name of the property.
     */
    protected bindEvents(bind: DataBindTarget)
    {
        for(let eventName of this.eventNames)
        {
            bind.element.addEventListener(eventName, e =>
            {
                if (e.currentTarget == bind.element)
                {
                    this.startValueUpdatePropagation(bind.propertyName, this.getElementValue(bind.element), bind.element);
                }
            });
        }
    }

    /**
     * Applies a value to all the bound elements.
     * @param propertyName Name of the property that changed.
     * @param value The new value.
     * @param senderElement The element that initiated the value change.
     */
    public set(propertyName: string, value: any, senderElement?: Element)
    {
        for(let bind of this.databinds)
        {
            if (this.shouldSetElementValue(bind.element, senderElement) && bind.propertyName == propertyName)
            {
                this.setElementValue(bind.element, value);
            }
        }
    }

    /** Executes all data binds. */
    public run()
    {
        for(let element of this.elements)
        {
            let propertyName = this.getPropertyValue(element);
            let bind: DataBindTarget = {
                propertyName: propertyName,
                element: element,
            };

            this.databinds.push(bind);
            
            this.loadDefaultValue(bind);
            this.bindEvents(bind);
        }
    }

    public applyDefaultValue(propertyName: string, value: any, senderElement?: Element)
    {
        this.startValueUpdatePropagation(propertyName, value, senderElement);
    }
}

export interface DataBindConstructor
{
    new (manager: DataBindManager, view: View): BaseDataBind;
}